<template>
  <BaseModal modal-content-class="modal_change_info">
    <div class="content">
      <form>
        <h1>{{ $_translations.value.modal_change_username_title }}</h1>

        <BaseTextFormInput
          :model-value="formValues.username.state"
          :label="$_translations.value.modal_change_username_input_label_username"
          :error="formValues.username.validation.error"
          :error-message="formValues.username.validation.message"
          @update:modelValue="formValues.username.setState"
        />

        <BaseButton
          container-class="update_info"
          :disabled="submitting"
          @click:button.prevent="handleSubmit"
        >
          {{ $_translations.value.modal_change_username_button_proceed }}
        </BaseButton>

        <TransparentCard container-class="inner">
          <b>{{ $_translations.value.modal_change_username_note_title }}</b>
          <p class="after_note">
            {{ $_translations.value.modal_change_username_note_description }}
          </p>
        </TransparentCard>
      </form>
    </div>

    <template v-if="isSuccess" #bottom>
      <p class="succ_upd">{{ $_translations.value.modal_change_username_success_message }}</p>
    </template>
  </BaseModal>
</template>

<script setup>
import { getCurrentInstance, ref } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import TransparentCard from '@/components/card/TransparentCard.vue';
import BaseTextFormInput from '@/components/formInput/BaseTextFormInput.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import useAuthStore from '@/store/modules/auth.js';
import useFormHook from '@/hooks/useForm.js';
import useUILockHook from '@/hooks/useUILock.js';
import { apiCheckUsernameAvailability } from '@/services/user.js';
import { usernameValidationConfiguration } from '@/constants/validation.js';

const { proxy } = getCurrentInstance();

const authStore = useAuthStore();

const { formValues, isFormInvalid, resetFormErrors } = useFormHook({ username: '' });
const [submitting, setSubmittingState] = useUILockHook();

const isSuccess = ref(false);

const validateForm = async () => {
  if (!formValues.username.state) {
    formValues.username.setValidation({ error: true, message: proxy.$_translations.value.common_error_input_field_required });
  } else if (formValues.username.state.length < usernameValidationConfiguration.minSize) {
    formValues.username.setValidation({ error: true, message: proxy.$_translations.value.common_error_username_min_size_restriction });
  } else if (formValues.username.state.length > usernameValidationConfiguration.maxSize) {
    formValues.username.setValidation({ error: true, message: proxy.$_translations.value.common_error_username_max_size_restriction });
  } else if (!formValues.username.state.match(usernameValidationConfiguration.formatRegex)) {
    formValues.username.setValidation({ error: true, message: proxy.$_translations.value.common_error_username_format_restriction });
  }

  if (isFormInvalid()) { return true; }

  const { parsedData } = await apiCheckUsernameAvailability({
    queryParams: {
      username: formValues.username.state,
      msisdn: authStore.user.msisdn,
    },
  });

  if (!parsedData.success) {
    let message;

    if (parsedData.message === 'Username exists') {
      message = proxy.$_translations.value.modal_change_username_error_username_exists + ' ' + parsedData.proposedUsername;
    } else if (parsedData.message === 'Invalid') {
      message = proxy.$_translations.value.modal_change_username_error_username_invalid;
    } else {
      message = proxy.$_translations.value.common_error_generic;
    }

    formValues.username.setValidation({ error: true, message });

    return true;
  }

  return false;
};

const handleSubmit = async () => {
  try {
    setSubmittingState(true);
    isSuccess.value = false;

    resetFormErrors();

    const invalid = await validateForm();

    if (invalid) { return; }

    await authStore.dispatchChangeUsername({ username: formValues.username.state });

    isSuccess.value = true;
  } catch {
    formValues.username.setValidation({ error: true, message: proxy.$_translations.value.modal_change_username_error_username_already_updated });
  } finally {
    setSubmittingState(false);
  }
};
</script>
